import { useRef, useState } from 'react';
import { apiUrl, resolveMediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import type { MicroPromoInfoType } from '@/components/MicroPromotionParamsPanel';

export type DefaultMomentsImageItem = {
  path: string;
  url: string;
};

type Props = {
  areaId: number;
  infoType: MicroPromoInfoType;
  items: DefaultMomentsImageItem[];
  onChange: (next: DefaultMomentsImageItem[]) => void;
  disabled?: boolean;
};

const MAX = 9;
const MIN = 1;

export function MicroPromotionDefaultMomentsImages({ areaId, infoType, items, onChange, disabled }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  async function uploadFiles(files: FileList | File[]) {
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (list.length === 0) return;
    const room = MAX - items.length;
    if (room < 1) return;
    const batch = list.slice(0, room);
    setUploading(true);
    try {
      const added: DefaultMomentsImageItem[] = [];
      for (const file of batch) {
        const fd = new FormData();
        fd.append('area_id', String(areaId));
        fd.append('info_type', infoType);
        fd.append('file', file);
        const res = await fetch(apiUrl('upload_micro_promotion_image'), {
          method: 'POST',
          body: fd,
          credentials: 'include',
        });
        const j = (await res.json()) as { ok?: boolean; path?: string; url?: string };
        if (j.ok && j.path) {
          added.push({
            path: j.path,
            url: j.url || resolveMediaUrl(j.path),
          });
        }
      }
      if (added.length > 0) {
        onChange([...items, ...added].slice(0, MAX));
      }
    } finally {
      setUploading(false);
    }
  }

  function removeAt(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function reorder(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
      return;
    }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  }

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-slate-500">
        启用发圈时须配置 <strong className="text-slate-700">{MIN}～{MAX}</strong> 张默认配图；拖拽缩略图可调整顺序（提交信息无图时使用）。
      </p>
      <div className="flex flex-wrap gap-2">
        {items.map((it, idx) => (
          <div
            key={`${it.path}-${idx}`}
            draggable={!disabled}
            onDragStart={() => setDragIdx(idx)}
            onDragEnd={() => {
              setDragIdx(null);
              setOverIdx(null);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setOverIdx(idx);
            }}
            onDragLeave={() => setOverIdx((v) => (v === idx ? null : v))}
            onDrop={(e) => {
              e.preventDefault();
              if (dragIdx !== null) reorder(dragIdx, idx);
              setDragIdx(null);
              setOverIdx(null);
            }}
            className={cn(
              'group relative h-24 w-24 cursor-grab overflow-hidden rounded-lg border bg-slate-50 active:cursor-grabbing',
              overIdx === idx && dragIdx !== null && dragIdx !== idx
                ? 'border-teal-500 ring-2 ring-teal-400/50'
                : 'border-slate-200',
            )}
            title="按住拖动可调整顺序"
          >
            <img src={it.url || resolveMediaUrl(it.path)} alt="" className="h-full w-full object-cover" draggable={false} />
            <span className="absolute left-1 top-1 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
              {idx + 1}
            </span>
            {!disabled ? (
              <button
                type="button"
                className="absolute right-1 top-1 rounded bg-white/90 px-1 text-xs text-rose-700 opacity-0 shadow group-hover:opacity-100"
                onClick={() => removeAt(idx)}
                aria-label="删除图片"
              >
                ×
              </button>
            ) : null}
          </div>
        ))}
        {items.length < MAX && !disabled ? (
          <button
            type="button"
            className="flex h-24 w-24 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 text-xs text-slate-500 hover:border-teal-400 hover:bg-teal-50/50 hover:text-teal-800"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? '上传中…' : '+ 添加图片'}
          </button>
        ) : null}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const fs = e.target.files;
          if (fs && fs.length > 0) void uploadFiles(fs);
          e.target.value = '';
        }}
      />
      {items.length > 0 && !disabled ? (
        <Button type="button" variant="ghost" onClick={() => onChange([])}>
          清空全部配图
        </Button>
      ) : null}
    </div>
  );
}
